import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import type { QRCodeScanner } from '@/lib/qr/QRCodeScanner';
import { useNavigate } from 'react-router-dom';
import { DataMatrixScanner } from '@/lib/qr/DataMatrixScanner';
import { useTranslation } from '@/hooks/useTranslation';
import { PageLayout } from '@/components/shared/PageLayout';
import { PageHeader } from '@/components/shared/PageHeader';
import { DropZoneCard } from '../components/DropZoneCard';
import { Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';

type ScanState = 'idle' | 'loading' | 'detected' | 'error';

export function UploadImage() {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [detectedId, setDetectedId] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [scanState, setScanState] = useState<ScanState>('idle');
  const [isDragOver, setIsDragOver] = useState(false);
  const scanner: QRCodeScanner = useMemo(() => new DataMatrixScanner(), []);
  const { t } = useTranslation();

  useEffect(() => {
    if (!detectedId) return;
    const timerId = window.setTimeout(() => {
      navigate(`/traceability/form?toolId=${encodeURIComponent(detectedId)}`);
    }, 400);
    return () => window.clearTimeout(timerId);
  }, [detectedId, navigate]);

  const drawToCanvas = useCallback(
    async (file: File) => {
      setScanState('loading');
      setStatus(t('traceability.uploadImage.statusLoading'));

      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;

      const dataUrl = await new Promise<string>((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result));
        fr.onerror = () => reject(fr.error);
        fr.readAsDataURL(file);
      });

      setPreviewUrl(dataUrl);

      const img = new Image();
      img.src = dataUrl;

      try {
        if (typeof img.decode === 'function') await img.decode();
        else {
          await new Promise<void>((res, rej) => {
            img.onload = () => res();
            img.onerror = (e) => rej(e);
          });
        }
      } catch {
        setScanState('error');
        setStatus(t('traceability.uploadImage.errorDecode'));
        return;
      }

      if (img.naturalWidth === 0 || img.naturalHeight === 0) {
        setScanState('error');
        setStatus(t('traceability.uploadImage.errorNoDimension'));
        return;
      }

      const maxDim = 1600;
      const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
      const w = Math.max(1, Math.round(img.naturalWidth * scale));
      const h = Math.max(1, Math.round(img.naturalHeight * scale));

      canvas.width = w;
      canvas.height = h;

      const ctx2 = canvas.getContext('2d')!;
      ctx2.save();
      ctx2.setTransform(1, 0, 0, 1, 0, 0);
      ctx2.globalCompositeOperation = 'source-over';
      ctx2.imageSmoothingEnabled = false;
      ctx2.fillStyle = '#fff';
      ctx2.fillRect(0, 0, w, h);
      ctx2.drawImage(img, 0, 0, w, h);
      ctx2.restore();

      setScanState('loading');
      setStatus(t('traceability.uploadImage.statusScanning'));
      const id = await scanner.scan(canvas);
      if (id) {
        setDetectedId(id);
        setScanState('detected');
        setStatus(t('traceability.uploadImage.statusDetected', { id }));
      } else {
        setScanState('error');
        setStatus(t('traceability.uploadImage.statusNotFound'));
      }
    },
    [scanner, t]
  );

  const onFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      await drawToCanvas(file);
      e.target.value = '';
    },
    [drawToCanvas]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (!file) return;
      await drawToCanvas(file);
    },
    [drawToCanvas]
  );

  const breadcrumbItems = [
    { label: t('common.brand'), onClick: () => navigate('/') },
    {
      label: t('traceability.scanPage.hero.breadcrumb'),
      onClick: () => navigate('/traceability/scan'),
    },
    { label: t('traceability.uploadImage.breadcrumb') },
  ];

  return (
    <PageLayout contentClassName="max-w-4xl">
      <PageHeader
        breadcrumb={breadcrumbItems}
        title={t('traceability.uploadImage.pageTitle')}
        description={t('traceability.uploadImage.pageDescription')}
      />

      <DropZoneCard
        previewUrl={previewUrl}
        isDragOver={isDragOver}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        inputRef={inputRef}
        onFileChange={onFileChange}
      />

      {/* Hidden canvas used only for scanning */}
      <canvas ref={canvasRef} className="sr-only" />

      {/* State-aware feedback cards */}
      {scanState === 'loading' && (
        <Callout tone="neutral" loading>
          {status}
        </Callout>
      )}

      {scanState === 'detected' && (
        <Callout
          tone="success"
          title={t('traceability.uploadImage.statusDetected', { id: detectedId ?? '' })}
        >
          <p className="font-mono text-base font-semibold">{detectedId}</p>
        </Callout>
      )}

      {scanState === 'error' && <Callout tone="error">{status}</Callout>}

      {/* Dev bypass */}
      {import.meta.env.DEV && (
        <Callout
          tone="warning"
          icon={Wrench}
          title={t('traceability.scanPage.dev.title')}
          action={
            <Button
              className="w-full"
              onClick={() => navigate(`/traceability/form?toolId=${encodeURIComponent('testing')}`)}
            >
              {t('traceability.scanPage.dev.button')}
            </Button>
          }
        >
          {t('traceability.scanPage.dev.description')}
        </Callout>
      )}
    </PageLayout>
  );
}
